import React, { useState } from 'react';
import useKYCStore from '../../store/kycStore';
import { FiUser, FiFileText, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import IconWrapper from '../print-steps/IconWrapper';
import { successToastStyle } from '../../utils/toastConfig';

const PersonalInfoStep: React.FC = () => {
  const { formData, updateFormData, nextStep, prevStep } = useKYCStore();
  const [errors, setErrors] = useState({
    owner_name: '',
    gst_number: '',
  });

  const validateForm = () => {
    const newErrors = {
      owner_name: '',
      gst_number: '',
    };
    let isValid = true;

    // Owner name validation - required
    if (!formData.owner_name.trim()) {
      newErrors.owner_name = 'Owner name is required';
      isValid = false;
    }

    // GST validation - optional but if provided, must be valid
    if (formData.gst_number && !isValidGST(formData.gst_number)) {
      newErrors.gst_number = 'Enter a valid GST number (Format: 22AAAAA0000A1Z5)';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Simple GST validation - format: 22AAAAA0000A1Z5
  const isValidGST = (gst: string) => {
    const regex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    return regex.test(gst);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      toast.success('Personal information saved!', successToastStyle);
      nextStep();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value });
  };

  return (
    <div className="max-w-2xl mx-auto px-2">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 sm:mb-6">Personal Information</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4 sm:space-y-6">
          {/* Owner Name Field */}
          <div>
            <label htmlFor="owner_name" className="block text-sm font-medium text-gray-700 mb-1">
              Owner Name <span className="text-red-500">*</span>
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IconWrapper icon={FiUser} className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="owner_name"
                name="owner_name"
                value={formData.owner_name}
                onChange={handleChange}
                className={`block w-full pl-10 pr-3 py-2 sm:text-sm border ${
                  errors.owner_name ? 'border-red-300' : 'border-gray-300'
                } rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                placeholder="Enter full name as per legal documents"
              />
            </div>
            {errors.owner_name && <p className="mt-1 text-sm text-red-600">{errors.owner_name}</p>}
          </div>

          {/* GST Number Field */}
          <div>
            <label htmlFor="gst_number" className="block text-sm font-medium text-gray-700 mb-1">
              GST Number <span className="text-gray-400 text-xs">(Optional)</span>
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IconWrapper icon={FiFileText} className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="gst_number"
                name="gst_number"
                value={formData.gst_number}
                onChange={handleChange}
                className={`block w-full pl-10 pr-3 py-2 sm:text-sm border ${
                  errors.gst_number ? 'border-red-300' : 'border-gray-300'
                } rounded-md uppercase focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                placeholder="eg: 22AAAAA0000A1Z5"
              />
            </div>
            {errors.gst_number && <p className="mt-1 text-sm text-red-600">{errors.gst_number}</p>}
            {!errors.gst_number && (
              <p className="mt-1 text-xs text-gray-500">
                If you don't have a GST number, you can leave this field empty.
              </p>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 sm:pt-6">
            <button
              type="button"
              onClick={prevStep}
              className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 shadow-sm text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <IconWrapper icon={FiChevronLeft} className="-ml-1 mr-1 sm:mr-2 h-4 w-4" />
              Back
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-xs sm:text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Next Step
              <IconWrapper icon={FiChevronRight} className="-mr-1 ml-1 sm:ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PersonalInfoStep;
